/**
 * Design constants for the LMS shell.
 *
 * Learn borrows Cloud's shell metrics rather than Scope's two-surface split —
 * every screen here is a member's or a teacher's, behind the session gate, and
 * nobody without a Mauvely account ever sees this app. So there is one theme,
 * user-toggled (`data-theme` on the root, flipped from the topbar), the same
 * arrangement as Cloud and website-enterprise.
 *
 * Everything below is `var(--*)` from the design system wherever the token set
 * has an answer. The literals are the handful of values the design (screens
 * 1a–1g) hard-coded outside it: 264 rail, 60 topbar, 38px nav rows, 22px panel
 * radius, 30/32/40 content padding.
 */

import type React from 'react';

export const EASE = 'cubic-bezier(.22,.61,.36,1)';
export const HAIR = '1px solid var(--border-subtle)';

export const LAYOUT = {
  rail: 264,
  topbar: 60,
  navRow: 38,
  /** Content padding: `30px 32px 40px` in the design. */
  contentPad: '30px 32px 40px',
  stackGap: 24,
  gridGap: 16
} as const;

export const RADIUS = {
  panel: 22,
  card: 16,
  control: 10,
  pill: 999
} as const;

export const SHADOW = {
  hairline: '0 1px 0 rgba(40,28,45,.04)',
  md: '0 4px 14px rgba(40,28,45,.08)',
  lg: '0 18px 40px rgba(40,28,45,.14)',
  popover: '0 18px 44px rgba(40,28,45,.18)'
} as const;

/** Baloo 2 800. Titles in the Mauvely voice end in a full stop. */
export const display = (size: number): React.CSSProperties => ({
  fontFamily: "var(--font-display), 'Baloo 2', system-ui",
  fontWeight: 800,
  fontSize: size,
  lineHeight: size >= 32 ? 1.02 : 1.1,
  letterSpacing: '-.005em',
  color: 'var(--text-strong)',
  margin: 0
});

/** Eyebrow labels: mono, wide tracking, uppercase. */
export const mono: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 10.5,
  letterSpacing: '.17em',
  textTransform: 'uppercase',
  color: 'var(--text-faint)'
};

/** Metadata lines: mono, joined with `·`, never commas and never emoji. */
export const monoPlain: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 11,
  letterSpacing: '.06em',
  color: 'var(--text-faint)'
};

/** Table column headers. */
export const tableHead: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 10,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  color: 'var(--text-quiet)'
};

/** A panel: white on the light surface, surface-card on the dark one. */
export const panel: React.CSSProperties = {
  background: 'var(--surface-card)',
  borderRadius: RADIUS.panel,
  boxShadow: SHADOW.hairline
};

/** The entrance every arriving block uses. */
export const entrance = `mvIn .3s ${EASE}`;

// ── origins ─────────────────────────────────────────────────────────────────

/**
 * Where the account lives.
 *
 * Copied from Cloud's `accountOrigin()`, localhost arm included: main runs on
 * :8787 in development and a hardcoded production origin would make the
 * sign-in round trip impossible to exercise locally.
 */
export function accountOrigin(): string {
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return `${protocol}//${hostname}:8787`;
  return 'https://mauvely.com';
}

/**
 * A hint, and nothing more.
 *
 * Somebody redirected off their company's own domain arrives on `/?org=<slug>`
 * (worker/index.ts's `resolveCustomDomain` redirect) and should go to that
 * company's branded sign-in page rather than Mauvely's. The slug picks a page;
 * it decides nothing about who this person is or what they may open once
 * signed in — `member_policy` does that, server-side, on every request. The
 * shape check is there so a query string cannot write an arbitrary path onto
 * the accounts origin.
 */
const ORG_HINT = /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/;

/** Round-trips through the main site, which owns sessions. It lands back here
 *  because `lms.mauvely.com` is in `RETURN_HOSTS` in website-main's
 *  shared/returnto.ts — without that entry this ends on /account instead. */
export const signInHref = (returnTo: string): string => {
  const hint =
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('org');
  const path = hint && ORG_HINT.test(hint) ? `/o/${hint}/signin` : '/signin';
  return `${accountOrigin()}${path}?next=${encodeURIComponent(returnTo)}`;
};

// ── formatting ──────────────────────────────────────────────────────────────

/**
 * Relative for the last week, then an absolute short date.
 *
 * Copied from the sibling apps' theme.ts rather than imported, because the web
 * apps share no package. If you change one, change the others.
 */
export function formatWhen(unixSeconds: number): string {
  const then = new Date(unixSeconds * 1000);
  const diff = Date.now() / 1000 - unixSeconds;

  if (diff < 60) return 'just now';
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  if (diff < 172800) return 'yesterday';
  if (diff < 604800) return then.toLocaleDateString('en-GB', { weekday: 'long' });
  return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** A due date, rendered in the reader's own locale: "Fri, 6 Aug". */
export function formatDeadline(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
}

/** "AR" for "Ada Rowe", falling back to the handle. Used for the avatar chip
 *  and for bundle-mate initials — the design never shows a photo here. */
export function initials(name: string, fallback: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  if (parts.length === 1 && parts[0]!.length >= 2) return parts[0]!.slice(0, 2).toUpperCase();
  return fallback.slice(0, 2).toUpperCase();
}

/** First name only, for the places the product speaks about a person. */
export const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name;

/**
 * A duration a person reads, from seconds.
 *
 * Rounded to the minute below an hour and to the minute above it, because the
 * only durations this app shows are lesson estimates and time spent, and
 * neither is meaningful to the second.
 */
export function secondsLabel(seconds: number): string {
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** The same, from the minutes a course wire carries. Null stays null so the
 *  caller drops the segment rather than printing "unknown". */
export const minutesLabel = (minutes: number | null): string | null =>
  minutes === null ? null : secondsLabel(minutes * 60);

/**
 * A mono metadata line: "8 lessons · 32 min · lesson 3 of 8".
 *
 * Middots, never commas, and anything unknown is dropped rather than rendered
 * as a dash — a line with a hole in it reads as a bug, and the fact it was
 * carrying was optional.
 */
export const meta = (...parts: (string | null | undefined | false)[]): string =>
  parts.filter((p): p is string => typeof p === 'string' && p.length > 0).join(' · ');

/** Pluralise on the count, which is the only rule this app needs. */
export const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n} ${n === 1 ? one : many}`;

/**
 * A colour value safe to put straight into an inline `background`.
 *
 * `course.colour` and `bundle.colour` reach the client as a bare string the
 * Worker does not yet constrain (`worker/routes/bundles.ts` — see
 * docs/BACKLOG.md). Until it does, this is the one gate between that string
 * and a CSS property: only a design-system `var(--token)` or a `#rrggbb`/`#rgb`
 * hex passes, and anything else — `url(...)` included — falls back to a
 * neutral tile rather than becoming a declaration.
 */
const SAFE_COLOUR = /^(var\(--[a-z0-9-]{1,40}\)|#[0-9a-f]{3}|#[0-9a-f]{6})$/i;
export const safeColour = (value: string): string =>
  SAFE_COLOUR.test(value.trim()) ? value : 'var(--slate-400)';

/** Tabular figures, so a column of numbers lines up. */
export const figure: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontVariantNumeric: 'tabular-nums'
};

/**
 * The deadline pill's words: "Overdue by 2 days" / "Due Friday" / "No deadline".
 *
 * The badge and the day count are both the server's — the deadline is
 * `MIN(due_at)` across three assignment scopes and this screen can see one of
 * them at most. All this does is choose the sentence.
 */
export function deadlineLabel(
  badge: 'overdue' | 'due' | 'none',
  dueAt: number | null,
  daysLate: number
): string {
  if (badge === 'none' || dueAt === null) return 'No deadline';
  if (badge === 'overdue') {
    return daysLate < 1 ? 'Overdue' : `Overdue by ${plural(daysLate, 'day')}`;
  }

  // Inside a week a weekday is the more useful word — "Due Friday" is a thing
  // somebody can plan around without counting. Beyond that it is ambiguous.
  const days = Math.floor((dueAt - Date.now() / 1000) / 86400);
  if (days <= 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days < 7) {
    return `Due ${new Date(dueAt * 1000).toLocaleDateString('en-GB', { weekday: 'long' })}`;
  }
  return `Due ${formatDeadline(dueAt)}`;
}

/**
 * A `ref` from a notification, as a path this app will navigate to.
 *
 * The rows come from the database and their `ref` is written by the Worker, but
 * the same guard Cloud's bell applies is applied here: a path, one leading
 * slash, no protocol and no `//`. A notification is the one place a stored
 * string becomes a destination, so it is checked at the point of use rather
 * than trusted for having been ours once.
 */
export function refPath(ref: string): string | null {
  if (!/^\/[A-Za-z0-9_\-/]{0,199}$/.test(ref)) return null;
  if (ref.includes('//')) return null;
  return ref;
}
